/**
 * The book's table of contents, as the default view of the index.
 *
 * THIS REPLACES A 720-ROW ALPHABETICAL DUMP as the first thing a visitor sees.
 * The A–Z list still exists at `/hymns` — it is what a crawler reads and it needs
 * no query string — but it answers "is hymn X in here", which is the question
 * somebody already holding the book has. The question people arrive with is
 * "what can we sing in Advent", and only the spine answers that.
 *
 * Grouped as the book groups: The Church Year and General Hymns carry a heading,
 * everything else sits flat, because the printed contents lists those as siblings
 * under no umbrella (see `hymnal-sections.ts`).
 */
import { Link } from 'react-router-dom'
import type { SectionPage } from '@gjl/music-manager/hymnal-pages'

export function SectionSpine({ sections, basePath }: { sections: readonly SectionPage[]; basePath: string }) {
  // Runs of consecutive sections sharing a group. Built by walking in book order
  // rather than bucketing by group — two separate runs of the same group would
  // otherwise silently merge and reorder the book.
  const runs: { group?: string; items: SectionPage[] }[] = []
  for (const s of sections) {
    const last = runs[runs.length - 1]
    if (last && last.group === s.group) last.items.push(s)
    else runs.push({ group: s.group, items: [s] })
  }

  return (
    <div className="space-y-6">
      {runs.map((run, i) => (
        <section key={run.group ?? `flat-${i}`} aria-label={run.group ?? 'Sections'}>
          {run.group && <h3 className="mb-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">{run.group}</h3>}
          <ul className="grid gap-x-6 sm:grid-cols-2">
            {run.items.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`${basePath}/section/${s.slug}`}
                  className="group flex min-h-11 touch-manipulation items-center justify-between gap-3 border-b border-border py-1.5 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <span className="text-sm text-muted-foreground group-hover:text-foreground">{s.label}</span>
                  <span className="shrink-0 font-mono text-xs text-muted-foreground">
                    {s.from}–{s.to}
                    <span className="ml-2 tabular-nums opacity-70">{s.hymns.length}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
